import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Code, Server, Mail, Brain, Shield, Database } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ProductCard } from '../components/ProductCard';
import { TestimonialCard } from '../components/TestimonialCard';
import { AnimatedSection } from '../components/AnimatedSection';
import { AnimatedCard } from '../components/AnimatedCard';
import { Wave } from '../components/Wave';
import { Users, Clock, CheckCircle, Gauge } from 'lucide-react';
import { TechnologySection } from '../components/TechnologySection';
import { useSEO } from '../hooks/useSEO';
import { getPageSEO } from '../data/seoData';

export const Home = () => {
  // SEO Configuration
  useSEO(getPageSEO('home'));

  const featuredProducts = [{
    title: 'QuickCont',
    description: 'Sistema contable integral diseñado para optimizar la gestión financiera de tu empresa',
    icon: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    path: '/quickcont'
  }, {
    title: 'Facelec',
    description: 'Solución completa para facturación electrónica que cumple con todas las normativas vigentes',
    icon: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    path: '/facelec'
  }, {
    title: 'Galenos Web',
    description: 'Sistema integral para la gestión de clínicas y consultorios médicos con herramientas especializadas',
    icon: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3',
    path: '/galenos'
  }];
  const stats = [{
    icon: <Users className="w-8 h-8" />,
    number: '1500+',
    label: 'Clientes Satisfechos',
    color: 'from-blue-500 to-blue-600'
  }, {
    icon: <Clock className="w-8 h-8" />,
    number: '5+',
    label: 'Años de Experiencia',
    color: 'from-green-500 to-green-600'
  }, {
    icon: <CheckCircle className="w-8 h-8" />,
    number: '50+',
    label: 'Proyectos Completados',
    color: 'from-purple-500 to-purple-600'
  }, {
    icon: <Gauge className="w-8 h-8" />,
    number: '99.99%',
    label: 'Disponibilidad de Servicios',
    color: 'from-red-500 to-red-600'
  }];
  const services = [{
    icon: <Code className="w-8 h-8" />,
    title: 'Desarrollo de Software',
    description: 'Soluciones personalizadas para tu negocio con las últimas tecnologías'
  }, {
    icon: <Server className="w-8 h-8" />,
    title: 'Servicios Cloud',
    description: 'Infraestructura escalable y segura en la nube'
  }, {
    icon: <Mail className="w-8 h-8" />,
    title: 'Email Marketing',
    description: 'Servicios de envío masivo de correos con alta entregabilidad'
  }, {
    icon: <Brain className="w-8 h-8" />,
    title: 'Inteligencia Artificial',
    description: 'Implementación de soluciones avanzadas con IA'
  }];
  const process = [{
    number: '01',
    title: 'Descubrimiento',
    description: 'Analizamos tus necesidades y objetivos'
  }, {
    number: '02',
    title: 'Planificación',
    description: 'Diseñamos la solución perfecta'
  }, {
    number: '03',
    title: 'Desarrollo',
    description: 'Implementamos tu solución'
  }, {
    number: '04',
    title: 'Lanzamiento',
    description: 'Desplegamos y optimizamos'
  }];
  const fadeUpVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="w-full">
      <section className="relative min-h-screen h-[125vh] md:h-screen overflow-hidden bg-blue-600">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-800 via-blue-700 to-blue-600">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNNTQuMDggMzBjMCAxMy4yNTUtMTAuNzQ1IDI0LTI0IDI0UzYuMDggNDMuMjU1IDYuMDggMzAgMTYuODI1IDYgMzAuMDggNnMyNCAxMC43NDUgMjQgMjR6IiBmaWxsPSJub25lIiBzdHJva2U9InJnYmEoMjU1LDI1NSwyNTUsMC4xKSIgc3Ryb2tlLXdpZHRoPSIuNSIvPjwvc3ZnPg==')] opacity-10" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent" />
          </div>
        </div>
        <div className="relative z-10 h-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-center w-full pt-10 md:pt-0">
              <motion.h1 initial={{
              opacity: 0,
              y: -20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} className="text-4xl md:text-6xl font-bold mb-6 text-white">
                Innova tu Negocio con Nuestras Soluciones
              </motion.h1>
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.8,
              delay: 0.4
            }} className="max-w-3xl mx-auto">
                <p className="text-xl mb-4 text-white/90">
                  Potencia tu organización con nuestras soluciones SaaS de
                  vanguardia diseñadas para la empresa moderna.
                </p>
                <p className="text-xl mb-8 text-white/90">
                  Además, ofrecemos desarrollo de software a medida para
                  adaptarnos a las necesidades específicas de tu negocio.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              delay: 0.6
            }} className="flex flex-col sm:flex-row justify-center items-center gap-4">
                <Link to="/products" className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl bg-white text-blue-600 hover:bg-blue-50 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                  Explorar Productos
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link to="/contact?showTech=true" state={{
                showTechnologies: true
              }} className="inline-flex items-center px-8 py-4 text-lg font-semibold rounded-xl border-2 border-white text-white hover:bg-white/10 transition-all duration-300">
                  Solicitar Desarrollo a Medida
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="animate-bounce">
            <div className="w-1 h-8 rounded-full bg-white/50 mx-auto"></div>
          </div>
        </motion.div>
      </section>
      <Wave color="#f9fafb" className="bg-blue-600" pattern="pattern1" />
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => <AnimatedCard key={index} delay={index * 0.1}>
                <motion.div className="relative overflow-hidden bg-white rounded-2xl shadow-lg p-6 group hover:shadow-xl transition-all duration-300 h-full flex flex-col" whileHover={{
              scale: 1.02
            }} transition={{
              duration: 0.2
            }}>
                  <div className="relative z-10 flex flex-col h-full">
                    <motion.div initial={{
                  scale: 0.5,
                  opacity: 0
                }} whileInView={{
                  scale: 1,
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className={`inline-flex p-3 rounded-lg bg-gradient-to-br ${stat.color} text-white mb-4`}>
                      {stat.icon}
                    </motion.div>
                    <motion.h3 initial={{
                  y: 20,
                  opacity: 0
                }} whileInView={{
                  y: 0,
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1 + 0.2
                }} className="text-4xl font-bold mb-2 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                      {stat.number}
                    </motion.h3>
                    <motion.p initial={{
                  y: 20,
                  opacity: 0
                }} whileInView={{
                  y: 0,
                  opacity: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1 + 0.3
                }} className="text-gray-600 font-medium">
                      {stat.label}
                    </motion.p>
                  </div>
                  <motion.div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} initial={false} />
                </motion.div>
              </AnimatedCard>)}
          </div>
        </div>
      </AnimatedSection>
      <Wave color="white" className="bg-gray-50" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={fadeUpVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Nuestros Servicios</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Ofrecemos una amplia gama de servicios de TI para ayudar a tu
              empresa a crecer y prosperar en la era digital.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => <AnimatedCard key={index} delay={index * 0.1}>
                <div className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 h-full flex flex-col">
                  <motion.div whileHover={{
                scale: 1.1,
                rotate: 5
              }} className="text-blue-600 mb-4">
                    {service.icon}
                  </motion.div>
                  <h3 className="text-xl font-semibold mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 flex-grow">
                    {service.description}
                  </p>
                </div>
              </AnimatedCard>)}
          </div>
        </div>
      </AnimatedSection>
      <Wave color="#f9fafb" className="bg-white" pattern="pattern3" />
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Cómo Trabajamos</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Un proceso probado para entregar soluciones de calidad.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, index) => <div key={index} className="relative p-6 rounded-lg ">
                <div className="text-5xl font-bold text-blue-500/80 mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
                {index < process.length - 1 && <div className="hidden lg:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2">
                    <ArrowRight className="w-6 h-6 text-blue-300" />
                  </div>}
              </div>)}
          </div>
        </div>
      </AnimatedSection>
      <Wave color="#2563eb" className="bg-gray-50" pattern="pattern1" />
      <AnimatedSection className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 variants={fadeUpVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} className="text-3xl font-bold text-white mb-4">
            ¿Listo para transformar tu negocio?
          </motion.h2>
          <motion.p variants={fadeUpVariants} initial="hidden" whileInView="visible" viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="text-white text-xl mb-8">
            Comienza hoy mismo con nuestras soluciones tecnológicas
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }}>
            <Link to="/contact" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-blue-600 bg-white hover:bg-gray-50 transition-all duration-300 transform hover:-translate-y-1">
              Contactar Ahora
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>;
};